import React, { useState, useEffect } from "react";
import IAddress from "../../../models/IAddress";
import INewAddress_Delivery_Window from "../../../models/INewAddress_Delivery_Window";
import OwCheckbox from "../OwCheckbox/OwCheckbox";
import OwLabel from "../OwLabel/OwLabel";
import OwToggle from "../OwToggle/OwToggle";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import OwTitle from "../OwTitle/OwTitle";
import DayWindowRow, { IDayWindowData } from "./DayWindowRow";
import "./DeliveryWindowsSection.scss";

// Day names for iteration
const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;
const DAY_LABELS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

type DayName = (typeof DAY_NAMES)[number];

interface DayWindowsState {
    sunday: IDayWindowData;
    monday: IDayWindowData;
    tuesday: IDayWindowData;
    wednesday: IDayWindowData;
    thursday: IDayWindowData;
    friday: IDayWindowData;
    saturday: IDayWindowData;
}

interface AllDaysWindowState {
    start_time: string;
    end_time: string;
    is_hard: boolean;
    soft_penalty_per_hour?: number;
}

export interface DeliveryWindowsData {
    has_delivery_window?: boolean;
    delivery_window_start?: string;
    delivery_window_end?: string;
    delivery_window_is_hard?: boolean;
    delivery_window_soft_penalty_per_hour?: number;
    Address_Delivery_Windows?: INewAddress_Delivery_Window[];
}

type Props = {
    address: IAddress;
    onDeliveryWindowsChange: (data: DeliveryWindowsData) => void;
};

const getDefaultDayWindow = (): IDayWindowData => ({
    enabled: false,
    start_time: "09:00",
    end_time: "17:00",
    is_hard: true,
    soft_penalty_per_hour: undefined,
});

const getDefaultAllDays = (): AllDaysWindowState => ({
    start_time: "09:00",
    end_time: "17:00",
    is_hard: true,
    soft_penalty_per_hour: undefined,
});

const getDefaultDayWindows = (): DayWindowsState => ({
    sunday: getDefaultDayWindow(),
    monday: { ...getDefaultDayWindow(), enabled: true },
    tuesday: { ...getDefaultDayWindow(), enabled: true },
    wednesday: { ...getDefaultDayWindow(), enabled: true },
    thursday: { ...getDefaultDayWindow(), enabled: true },
    friday: { ...getDefaultDayWindow(), enabled: true },
    saturday: getDefaultDayWindow(),
});

const constraintOptions = [
    { value: "hard", label: "Hard" },
    { value: "soft", label: "Soft" },
];

export const DeliveryWindowsSection = (props: Props) => {
    const { address, onDeliveryWindowsChange } = props;

    // Determine initial mode based on existing data
    const hasDaySpecificWindows = address.Address_Delivery_Windows && address.Address_Delivery_Windows.length > 0;

    const [useSameForAllDays, setUseSameForAllDays] = useState(!hasDaySpecificWindows);
    const [allDaysWindow, setAllDaysWindow] = useState<AllDaysWindowState>(() => {
        if (address.delivery_window_start || address.delivery_window_end) {
            return {
                start_time: address.delivery_window_start || "09:00",
                end_time: address.delivery_window_end || "17:00",
                is_hard: address.delivery_window_is_hard ?? true,
                soft_penalty_per_hour: address.delivery_window_soft_penalty_per_hour,
            };
        }
        return getDefaultAllDays();
    });

    const [dayWindows, setDayWindows] = useState<DayWindowsState>(() => {
        if (hasDaySpecificWindows) {
            const windows = getDefaultDayWindows();
            address.Address_Delivery_Windows!.forEach((w) => {
                const dayName = DAY_NAMES[w.day_of_week];
                if (dayName) {
                    windows[dayName] = {
                        enabled: true,
                        start_time: w.start_time,
                        end_time: w.end_time,
                        is_hard: w.is_hard ?? true,
                        soft_penalty_per_hour: w.soft_penalty_per_hour,
                    };
                }
            });
            return windows;
        }
        return getDefaultDayWindows();
    });

    // Propagate changes to parent whenever state changes
    useEffect(() => {
        const data = transformToAddressData();
        onDeliveryWindowsChange(data);
    }, [useSameForAllDays, allDaysWindow, dayWindows]);

    const transformToAddressData = (): DeliveryWindowsData => {
        if (useSameForAllDays) {
            return {
                delivery_window_start: allDaysWindow.start_time,
                delivery_window_end: allDaysWindow.end_time,
                delivery_window_is_hard: allDaysWindow.is_hard,
                delivery_window_soft_penalty_per_hour: allDaysWindow.is_hard ? undefined : allDaysWindow.soft_penalty_per_hour,
                Address_Delivery_Windows: [], // Clear day-specific
            };
        } else {
            const windows: INewAddress_Delivery_Window[] = [];
            DAY_NAMES.forEach((dayName, index) => {
                const dayData = dayWindows[dayName];
                if (dayData.enabled) {
                    windows.push({
                        address_id: address.id,
                        day_of_week: index,
                        start_time: dayData.start_time,
                        end_time: dayData.end_time,
                        is_hard: dayData.is_hard,
                        soft_penalty_per_hour: dayData.is_hard ? undefined : dayData.soft_penalty_per_hour,
                    });
                }
            });
            return {
                delivery_window_start: undefined,
                delivery_window_end: undefined,
                delivery_window_is_hard: undefined,
                delivery_window_soft_penalty_per_hour: undefined,
                Address_Delivery_Windows: windows,
            };
        }
    };

    const handleModeToggle = (e: any) => {
        setUseSameForAllDays(e.target.value);
    };

    const handleAllDaysTimeChange = (field: "start_time" | "end_time") => (e: React.ChangeEvent<HTMLInputElement>) => {
        setAllDaysWindow((prev) => ({
            ...prev,
            [field]: e.target.value,
        }));
    };

    const handleAllDaysConstraintChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setAllDaysWindow((prev) => ({
            ...prev,
            is_hard: e.target.value === "hard",
        }));
    };

    const handleAllDaysPenaltyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value === "" ? undefined : parseFloat(e.target.value);
        setAllDaysWindow((prev) => ({
            ...prev,
            soft_penalty_per_hour: value,
        }));
    };

    const handleDayWindowChange = (dayName: string, field: keyof IDayWindowData, value: any) => {
        setDayWindows((prev) => ({
            ...prev,
            [dayName]: {
                ...prev[dayName as DayName],
                [field]: value,
            },
        }));
    };

    const handleToggleDeliveryWindow = (e: any) => {
        const enabled = e.target.value;
        onDeliveryWindowsChange({
            ...transformToAddressData(),
            has_delivery_window: enabled,
        });
    };

    return (
        <div className="DeliveryWindowsSection">
            <OwTitle title="delivery_windows" tag="h5" leftIconName="fad fa-clock" className="mb-2" />

            <div className="mb-3">
                <OwToggle
                    fn={handleToggleDeliveryWindow}
                    name="has_delivery_window"
                    label="has_delivery_windows"
                    active={address.has_delivery_window !== false}
                    labelOnRight={true}
                />
            </div>

            {address.has_delivery_window !== false && (
                <>
                    <div className="mb-3">
                        <OwToggle
                            fn={handleModeToggle}
                            name="useSameForAllDays"
                            label="same_for_all_days"
                            active={useSameForAllDays}
                            labelOnRight={true}
                        />
                    </div>

                    {useSameForAllDays ? (
                        <div className="all-days-section">
                            <div className="d-flex align-items-center mb-2">
                                <div className="me-3">
                                    <OwLabel title="start_time" noBottomMargin={true} />
                                    <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        style={{ width: "130px" }}
                                        value={allDaysWindow.start_time}
                                        onChange={handleAllDaysTimeChange("start_time")}
                                    />
                                </div>
                                <div>
                                    <OwLabel title="end_time" noBottomMargin={true} />
                                    <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        style={{ width: "130px" }}
                                        value={allDaysWindow.end_time}
                                        onChange={handleAllDaysTimeChange("end_time")}
                                    />
                                </div>
                            </div>

                            <div className="d-flex align-items-center mb-2">
                                <div className="me-3">
                                    <OwLabel title="constraint" noBottomMargin={true} />
                                    <OwSelectOption
                                        name="all_days_constraint"
                                        options={constraintOptions}
                                        valueProp="value"
                                        titleProp="label"
                                        selectedOption={{ constraint: allDaysWindow.is_hard ? "hard" : "soft" }}
                                        selectedOptionProp="constraint"
                                        fn={handleAllDaysConstraintChange}
                                        sizeClass="form-control-sm"
                                    />
                                </div>

                                {!allDaysWindow.is_hard && (
                                    <>
                                        <div>
                                            <OwLabel title="priority" noBottomMargin={true} />
                                            <div className="d-flex align-items-center">
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    value={allDaysWindow.soft_penalty_per_hour ?? ""}
                                                    onChange={handleAllDaysPenaltyChange}
                                                    placeholder="0"
                                                    min="0"
                                                    step="1"
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div>
                                <OwLabel
                                    leftIconClass="fas fa-info-circle"
                                    title="learn_more"
                                    noBottomMargin={true}
                                    fn={() => {
                                        // open in a new tab and go to /learn/delivery-windows
                                        window.open("https://www.orderwerks.com/learn/delivery-windows", "_blank");
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="day-specific-section">
                                {DAY_NAMES.map((dayName, index) => (
                                    <DayWindowRow
                                        key={dayName}
                                        dayName={dayName}
                                        dayLabel={DAY_LABELS[index]}
                                        data={dayWindows[dayName]}
                                        onChange={handleDayWindowChange}
                                    />
                                ))}
                            </div>
                            <div>
                                <OwLabel
                                    leftIconClass="fas fa-info-circle"
                                    title="learn_more"
                                    noBottomMargin={true}
                                    fn={() => {
                                        // open in a new tab and go to /learn/delivery-windows
                                        window.open("https://www.orderwerks.com/learn/delivery-windows", "_blank");
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default DeliveryWindowsSection;
